import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Landmark, ShieldCheck, UserRoundCheck } from "lucide-react";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../../store";
import {
  getMyPayments,
  refreshKycPaymentStatus,
} from "../../../../Redux/customer_kyc/slice";
import BankAccVerificationTable from "./BankAccVerificationTable";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import "./BankAccountVerificationPage.css";

const formatCurrency = (value: number) =>
  `₹${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

const BankAccVerification = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { myPayments } = useSelector((state: RootState) => state.customerKyc);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // The Java GET /charges/my-payments endpoint supports only `page` and `limit`.
  useEffect(() => {
    dispatch(getMyPayments({ page, limit }));
  }, [dispatch, page, limit]);

  const handleRefreshPayment = (paymentId: string) => {
    dispatch(refreshKycPaymentStatus(paymentId)).then((res) => {
      if (refreshKycPaymentStatus.fulfilled.match(res)) {
        toast.success("Payment status refreshed successfully");
        dispatch(getMyPayments({ page, limit }));
      } else {
        toast.error("Failed to refresh payment status");
      }
    });
  };

  const payments = myPayments?.data || [];
  const totalRecords = myPayments?.pagination?.total ?? payments.length;

  const successCount = payments.filter(
    (payment) =>
      (payment.paymentStatus || "").toLowerCase() === "success",
  ).length;
  const pendingCount = payments.filter(
    (payment) =>
      (payment.paymentStatus || "").toLowerCase() === "pending",
  ).length;
  const totalCharges = payments.reduce(
    (total, payment) => total + (payment.charges || 0),
    0,
  );

  const resultStart =
    totalRecords === 0 ? 0 : (page - 1) * limit + 1;
  const resultEnd = Math.min(
    page * limit,
    resultStart + payments.length - 1,
  );

  return (
    <section className="bank-verification-page">
      <div className="bank-verification-shell">
        <header className="bank-verification-header">
          <div className="bank-verification-heading">
            <div className="bank-verification-heading-icon">
              <Landmark size={23} strokeWidth={2.2} />
            </div>

            <div className="bank-verification-heading-copy">
              <span className="bank-verification-eyebrow">
                Withdrawal
              </span>
              <h1>Bank Account Verification</h1>
              <p>
                Review verification attempts, bank information,
                transaction IDs and verification charges.
              </p>
            </div>
          </div>

          <div className="bank-verification-summary">
            <div className="bank-verification-summary-card">
              <span>Verified</span>
              <strong>{successCount}</strong>
            </div>
            <div className="bank-verification-summary-card">
              <span>Pending</span>
              <strong>{pendingCount}</strong>
            </div>
            <div className="bank-verification-summary-card bank-verification-summary-card-primary">
              <span>Charges</span>
              <strong>{formatCurrency(totalCharges)}</strong>
            </div>
          </div>
        </header>

        <div className="bank-verification-toolbar">
          <div className="bank-verification-toolbar-left">
            <select
              value={limit}
              aria-label="Entries per page"
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 75, 100].map((option) => (
                <option key={option} value={option}>
                  {option} Entries
                </option>
              ))}
            </select>

            <span className="bank-verification-record-count">
              {totalRecords} records
            </span>
          </div>
        </div>

        <BankAccVerificationTable
          payments={payments}
          loading={myPayments?.loading || false}
          onRefresh={handleRefreshPayment}
          resultStart={resultStart}
        />

        <footer className="bank-verification-footer">
          <div className="bank-verification-results">
            Showing <strong>{payments.length ? resultStart : 0}</strong> -{" "}
            <strong>{payments.length ? resultEnd : 0}</strong> of{" "}
            <strong>{totalRecords}</strong>
          </div>

          <div className="bank-verification-pagination">
            {myPayments?.pagination && (
              <Pagenation
                currentPage={myPayments.pagination.page}
                totalPages={myPayments.pagination.totalPages}
                onPageChange={setPage}
              />
            )}
          </div>
        </footer>

        <div className="bank-verification-note">
          <ShieldCheck size={18} strokeWidth={2.2} />
          <span>
            Bank details are shown in a structured verification card
            inside the table for better readability across desktop,
            tablet and mobile widths.
          </span>
          <span className="bank-verification-note-badge">
            <UserRoundCheck size={14} />
            Secure verification
          </span>
        </div>
      </div>
    </section>
  );
};

export default BankAccVerification;
